import React, { useEffect, useMemo, useState } from "react";
import { Grid, GridColumn, GridToolbar } from "@progress/kendo-react-grid";
import { Loader } from "@progress/kendo-react-indicators";
import { printIcon } from "@progress/kendo-svg-icons";
import { SvgIcon } from "@progress/kendo-react-common";
import LoadingScreen from "components/common/Loading";
import ReportViewer from "pages/Reports/ReportViewerComponent";
import { ReportURL } from "api/axios.config";
import { getMatterLedgerReportName } from "pages/MatterLedger/matterLedgerTabs";
import styles from "./LedgerGrid.module.scss";
import {
  getLedgerCellValue,
  cellWithBackGround,
  RefCell,
  CenterTextHeaderCell,
} from "./LedgerGridCells";
import {
  DateFormatCell,
  AmountFormatCellEmptyZero,
  AmountFormat,
} from "components/common/formatter";
import excelIcon from "assets/fileIcons/Excel.svg";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { usePreviousTransactions } from "components/common/PreviousTransactions/usePreviousTransactions";
import { GRID_PAGE_SIZES } from "components/common/GridComponents";

const PreviousTransactionsGrid = ({
  isOpen,
  entity,
  title,
  onClose,
  showDDA,
  onExport,
  isExporting,
  handleActionRef,
}) => {
  const [showReport, setShowReport] = useState(false);
  const { dataState, setDataState, loading, transactions } =
    usePreviousTransactions(isOpen ? entity : null, "matter");

  useEffect(() => {
    if (!isOpen) setShowReport(false);
  }, [isOpen]);

  const reportTab = useMemo(
    () => ({
      type: entity?.ledgerPostingType || "ALL",
      currencyCode: entity?.currencyCode ?? null,
    }),
    [entity?.ledgerPostingType, entity?.currencyCode],
  );

  const reportParameters = useMemo(
    () => ({
      matterId: entity?.matterId,
      reportDocumentName: entity?.matterRef
        ? `Matter Ledger - ${entity.matterRef}`
        : "Ledger",
      ReportType: "Previous",
      ...(entity?.ledgerPostingType &&
        entity.ledgerPostingType !== "ALL" && {
          LedgerPostingType: entity.ledgerPostingType,
        }),
      ...(entity?.currencyCode && { CurrencyCode: entity.currencyCode }),
    }),
    [entity],
  );

  return (
    <>
    <Dialog
      onClose={onClose}
      title={title}
      className="dialog-xxl dialog-title-md"
    >
      <div className="m-4">
        {loading && <LoadingScreen />}
        <Grid
          data={transactions.data}
          total={transactions.total}
          skip={dataState.skip}
          take={dataState.take}
          pageable={{
            buttonCount: 5,
            pageSizes: GRID_PAGE_SIZES,
            previousNext: true,
          }}
          onDataStateChange={(e) => setDataState(e.dataState)}
          style={{
            height: "645px",
          }}
        >
          <GridToolbar>
            <button
              className="btn p-0 border-0"
              onClick={() => setShowReport(true)}
              title="Print"
              disabled={!entity?.matterId}
            >
              <div className={styles["dark-background"]}>
                <SvgIcon icon={printIcon} size="default" color="blue" />
              </div>
            </button>
            <button
              className="btn p-0 border-0"
              onClick={() => onExport(true)}
              title="Export to Excel"
              disabled={!transactions.data.length || isExporting}
            >
              <div className={styles["dark-background"]}>
                {isExporting ? (
                  <Loader
                    size="small"
                    type="infinite-spinner"
                    className="text-dark"
                  />
                ) : (
                  <img
                    src={excelIcon}
                    width={18}
                    height={18}
                    alt="Export to Excel"
                  />
                )}
              </div>
            </button>
          </GridToolbar>

          <GridColumn>
            <GridColumn
              field="date"
              title="Date"
              width="100px"
              cell={DateFormatCell}
            />
            <GridColumn field="postingType" title="Type" width="55px" />
            <GridColumn
              field="reference"
              title="Ref"
              width="110px"
              cell={(props) =>
                RefCell({ ...props, isPrevTrans: true }, handleActionRef)
              }
              className="text-wrap"
            />
            <GridColumn
              field="details"
              title="Details"
              width="320px"
              className="text-prewrap"
            />
            <GridColumn
              field="vatAmount"
              title="VAT"
              width="90px"
              headerCell={CenterTextHeaderCell}
              cell={AmountFormatCellEmptyZero}
            />
          </GridColumn>

          <GridColumn title="OFFICE" headerCell={CenterTextHeaderCell}>
            <GridColumn
              field="grossAmount"
              title="Debit"
              headerCell={CenterTextHeaderCell}
              cell={(e) => cellWithBackGround(e, "OFFICE", "Debit")}
              width="95px"
            />
            <GridColumn
              field="grossAmount"
              title="Credit"
              headerCell={CenterTextHeaderCell}
              cell={(e) => cellWithBackGround(e, "OFFICE", "Credit")}
              width="95px"
            />
            <GridColumn
              field="balance"
              title="Balance"
              cell={(e) => cellWithBackGround(e, "OFFICE", "Amount")}
              width="95px"
            />
          </GridColumn>

          <GridColumn title="CLIENT" headerCell={CenterTextHeaderCell}>
            <GridColumn
              field="clientAmount"
              title="Debit"
              headerCell={CenterTextHeaderCell}
              cell={(e) => cellWithBackGround(e, "CLIENT", "Debit")}
              width="95px"
            />
            <GridColumn
              field="clientAmount"
              title="Credit"
              headerCell={CenterTextHeaderCell}
              cell={(e) => cellWithBackGround(e, "CLIENT", "Credit")}
              width="95px"
            />
            <GridColumn
              field="clientBalance"
              title="Balance"
              headerCell={CenterTextHeaderCell}
              cell={(e) => cellWithBackGround(e, "CLIENT", "Amount")}
              width="110px"
            />
          </GridColumn>

          {showDDA && (
            <GridColumn title="DDA" headerCell={CenterTextHeaderCell}>
              <GridColumn
                field="ddaAmount"
                title="Debit"
                headerCell={CenterTextHeaderCell}
                cell={(e) => cellWithBackGround(e, "DDA", "Debit")}
                width="95px"
              />
              <GridColumn
                field="ddaAmount"
                title="Credit"
                headerCell={CenterTextHeaderCell}
                cell={(e) => cellWithBackGround(e, "DDA", "Credit")}
                width="95px"
              />
              <GridColumn
                field="ddaBalance"
                title="Balance"
                headerCell={CenterTextHeaderCell}
                cell={(e) => cellWithBackGround(e, "DDA", "Amount")}
                width="95px"
              />
            </GridColumn>
          )}
        </Grid>
      </div>

      <DialogActionsBar>
        <div className="dialog-footer d-flex justify-content-end">
          <button className="common-btn me-5" onClick={onClose}>
            Close
          </button>
        </div>
      </DialogActionsBar>
    </Dialog>

    {showReport && (
      <Dialog
        title="Matter Previous Transactions Report"
        onClose={() => setShowReport(false)}
        className="dialog pdfwindow fs-md"
        minWidth="60vw"
      >
        <div className="px-3 pt-1" style={{ minHeight: "65vh" }}>
          <ReportViewer
            modalHeight="65vh"
            name={getMatterLedgerReportName(reportTab)}
            parameters={reportParameters}
            url={`${ReportURL}/Reporting/reports`}
          />
        </div>
        <div className="dialog-footer">
          <button
            className="common-btn me-5"
            type="button"
            onClick={() => setShowReport(false)}
          >
            Close
          </button>
        </div>
      </Dialog>
    )}
    </>
  );
};

export default PreviousTransactionsGrid;
